import { useEffect, useMemo, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import Select from "react-select";
import LocationServices from "../../../services/LocationServices";
import CashBookPrintServices from "../../../services/CashBookPrintServices";

const CashBookPrint = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedStore, setSelectedStore] = useState("");
  const [selectedPassbook, setSelectedPassbook] = useState("");

  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storeError, setStoreError] = useState("");

  const [passbooks, setPassbooks] = useState([]);
  const [passbooksLoading, setPassbooksLoading] = useState(false);
  const [passbookError, setPassbookError] = useState("");

  const [open, setOpen] = useState(true);
  const [open2, setOpen2] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadStores = async () => {
      setStoresLoading(true);
      setStoreError("");

      try {
        const user = JSON.parse(
          localStorage.getItem("userDetails") || "null"
        );

        const role = String(
          user?.data?.user?.role || ""
        ).toLowerCase();

        let storeList = [];

       if (["superadmin", "accounts"].includes(role)) {
          const response = await LocationServices.getStore();
          storeList = response?.data?.data;

          if (!Array.isArray(storeList)) {
            throw new Error("Unexpected store API response.");
          }
        } else {
          const assignedStores = user?.data?.user?.store;

          storeList = Array.isArray(assignedStores)
            ? assignedStores
            : [];
        }

        if (!cancelled) {
          setStores(storeList.filter((store) => store?._id));
        }
      } catch (error) {
        if (!cancelled) {
          setStores([]);
          setStoreError(
            error?.response?.data?.message ||
              error?.message ||
              "Unable to load stores."
          );
        }
      } finally {
        if (!cancelled) {
          setStoresLoading(false);
        }
      }
    };

    loadStores();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    setPassbooks([]);
    setSelectedPassbook("");
    setPassbookError("");

    if (!selectedStore) {
      setPassbooksLoading(false);
      return;
    }

    const loadPassbooks = async () => {
      setPassbooksLoading(true);

      try {
        const response =
          await CashBookPrintServices.getPassbooksByStore(
            selectedStore
          );

        // Supports an unwrapped response or an Axios response.
        const body = response?.data?.status
          ? response.data
          : response;

        const list = body?.data?.passbooks;

        if (!Array.isArray(list)) {
          throw new Error("Unexpected passbook API response.");
        }

        // Ignore an old response if the location changed.
        if (!cancelled) {
          setPassbooks(list);
        }
      } catch (error) {
        if (!cancelled) {
          setPassbooks([]);
          setPassbookError(
            error?.response?.data?.message ||
              error?.message ||
              "Unable to load passbooks."
          );
        }
      } finally {
        if (!cancelled) {
          setPassbooksLoading(false);
        }
      }
    };

    loadPassbooks();

    return () => {
      cancelled = true;
    };
  }, [selectedStore]);

  const storeOptions = useMemo(
    () =>
      stores.map((store) => ({
        value: String(store._id),
        label:
          [store.name, store.location]
            .filter(Boolean)
            .join(" / ") || "Store"
      })),
    [stores]
  );

  const passbookOptions = useMemo(
    () =>
      passbooks
        .filter((passbook) => passbook?._id)
        .map((passbook) => ({
          value: String(passbook._id),
          label:
            (passbook.name || "Passbook") +
            (passbook.status === "inactive" ? " (Inactive)" : "")
        })),
    [passbooks]
  );

  const handleStoreChange = (option) => {
    const storeId = option?.value || "";

    if (storeId === selectedStore) return;

    // Clear old options immediately when changing location.
    setSelectedPassbook("");
    setPassbooks([]);
    setPassbookError("");
    setPassbooksLoading(Boolean(storeId));
    setSelectedStore(storeId);
  };

  return (
    <div className="col-xl-12">
      {/* Section 1 */}
      <div className="filter cm-content-box box-primary">
        <div className="content-title">
          <div className="cpa">
            <i className="fa fa-book me-2" aria-hidden="true" />
            CashBook Print
          </div>

          <div className="tools">
            <button
              type="button"
              className={`SlideToolHeader ${
                open ? "collapse" : "expand"
              }`}
              onClick={() => setOpen((previous) => !previous)}
              aria-label="Toggle CashBook selection"
              aria-expanded={open}
              style={{ border: 0, background: "transparent" }}
            >
              <i
                className={`fas ${
                  open ? "fa-angle-up" : "fa-angle-down"
                }`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <Collapse in={open}>
          <div className="cm-content-body form excerpt">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-6 col-xl-3">
                  <label
                    htmlFor="cashbook-date"
                    className="form-label"
                  >
                    Select Date
                  </label>

                  <input
                    id="cashbook-date"
                    type="date"
                    className="form-control"
                    value={selectedDate}
                    onChange={(event) =>
                      setSelectedDate(event.target.value)
                    }
                  />
                </div>

                <div className="col-12 col-md-6 col-xl-4">
                  <label
                    htmlFor="cashbook-store"
                    className="form-label"
                  >
                    Store / Location
                  </label>

                  <Select
                    inputId="cashbook-store"
                    instanceId="cashbook-store-select"
                    className="custom-react-select"
                    classNamePrefix="react-select"
                    options={storeOptions}
                    value={
                      storeOptions.find(
                        (option) => option.value === selectedStore
                      ) || null
                    }
                    onChange={handleStoreChange}
                    placeholder="Select Store / Location"
                    isSearchable
                    isClearable
                    isLoading={storesLoading}
                    isDisabled={storesLoading}
                    menuPlacement="auto"
                    noOptionsMessage={() => "No stores found"}
                  />
                </div>

                <div className="col-12 col-md-6 col-xl-3">
                  <label
                    htmlFor="cashbook-passbook"
                    className="form-label"
                  >
                    Passbook
                  </label>

                  <Select
                    inputId="cashbook-passbook"
                    instanceId="cashbook-passbook-select"
                    className="custom-react-select"
                    classNamePrefix="react-select"
                    options={passbookOptions}
                    value={
                      passbookOptions.find(
                        (option) =>
                          option.value === selectedPassbook
                      ) || null
                    }
                    onChange={(option) =>
                      setSelectedPassbook(option?.value || "")
                    }
                    placeholder={
                      !selectedStore
                        ? "Select Location First"
                        : passbooksLoading
                          ? "Loading Passbooks..."
                          : "Select Passbook"
                    }
                    isSearchable
                    isClearable
                    isLoading={passbooksLoading}
                    isDisabled={
                      !selectedStore ||
                      storesLoading ||
                      passbooksLoading
                    }
                    menuPlacement="auto"
                    noOptionsMessage={() =>
                      passbookError
                        ? "Unable to load passbooks"
                        : "No passbooks for this location"
                    }
                  />
                </div>

                <div className="col-12 col-md-6 col-xl-2">
                  <button
                    type="button"
                    className="btn btn-primary w-100"
                    disabled={
                      storesLoading ||
                      passbooksLoading ||
                      !selectedDate ||
                      !selectedStore ||
                      !selectedPassbook
                    }
                  >
                    Generate
                  </button>
                </div>
              </div>

              {storeError && (
                <div className="text-danger mt-2" role="alert">
                  {storeError}
                </div>
              )}

              {passbookError && (
                <div className="text-danger mt-2" role="alert">
                  {passbookError}
                </div>
              )}
            </div>
          </div>
        </Collapse>
      </div>

      {/* Section 2 — blank */}
      <div className="filter cm-content-box box-primary">
        <div className="content-title">
          <div className="cpa">
            <i className="fa fa-list me-2" aria-hidden="true" />
            CashBook Report
          </div>

          <div className="tools">
            <button
              type="button"
              className={`SlideToolHeader ${
                open2 ? "collapse" : "expand"
              }`}
              onClick={() => setOpen2((previous) => !previous)}
              aria-label="Toggle CashBook report"
              aria-expanded={open2}
              style={{ border: 0, background: "transparent" }}
            >
              <i
                className={`fas ${
                  open2 ? "fa-angle-up" : "fa-angle-down"
                }`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <Collapse in={open2}>
          <div className="cm-content-body form excerpt">
            <div
              className="card-body"
              style={{ minHeight: "180px" }}
            />
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default CashBookPrint;